/**
 * Visibility rehydrate.
 *
 * Quando uma PWA/aba retorna do background, o Supabase v2 às vezes:
 *   - dispara SIGNED_OUT espúrio (token "perdido" em memória mas presente
 *     no storage), ou
 *   - mantém uma sessão stale que precisa de refresh.
 *
 * Padrão correto: ao receber `visibilitychange → visible`, chamar
 * `getSession()` e re-aplicar a sessão atual. Se for null, não inventa nada.
 */
import type { Session } from "@supabase/supabase-js";
import type { PpsSupabaseClient } from "../client";

export interface VisibilityRehydrateConfig {
  client: PpsSupabaseClient;
  onSession: (session: Session | null) => void;
  /** Override para testes. Default: `globalThis.document`. */
  doc?: Document;
}

/**
 * Registra listener `visibilitychange` que re-busca a sessão e chama
 * `onSession`. Devolve cleanup. Em ambientes sem `document`, retorna no-op.
 */
export function registerVisibilityRehydrate(
  config: VisibilityRehydrateConfig
): () => void {
  const doc = config.doc ?? (typeof document !== "undefined" ? document : undefined);
  if (!doc) return () => {};

  const handler = () => {
    if (doc.visibilityState !== "visible") return;
    config.client.auth
      .getSession()
      .then(({ data }) => {
        config.onSession(data.session);
      })
      .catch(() => {
        // getSession failure não derruba sessão atual — ignoramos
      });
  };

  doc.addEventListener("visibilitychange", handler);
  return () => doc.removeEventListener("visibilitychange", handler);
}
